import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Caption1, MessageBar, MessageBarBody, Text, makeStyles } from '@fluentui/react-components';
import { shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { fileCategoryLabels, offerAcceptByText, type HrWorkflowView, type OfferRecord, type Person } from '@vigeo/shared';
import { OfferCard, OfferValues, rateText, reachedStage } from '@/features/offers';
import { fetchOfferPreview, performHrWorkflow } from '@/features/hr-workflow';
import { PortalPreview } from './PortalPreview.js';

/**
 * Row 1.3 — frame 06b `295:801`. HR reads the offer the way the candidate will. Drawn inline in
 * Offer and stage (#440) and, through `OfferReviewPage`, on its own route.
 *
 * The whole point is that there is no second rendering: the page asks the server for the very
 * artifact the issue path files and puts it through `PdfFormPortal`, the same component the
 * portal runs. A preview drawn from the typed terms instead would let a reviewer approve
 * something other than what goes out.
 */

const useStyles = makeStyles({
  preview: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, padding: `0 ${tokens.spacingHorizontalXL} ${tokens.spacingVerticalL}`, minWidth: 0 },
  caption: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  captionHead: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold },
  checks: {
    display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: tokens.spacingHorizontalL, flexWrap: 'wrap',
    padding: tokens.spacingHorizontalXL, minWidth: 0,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  rates: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, flexGrow: 1, flexBasis: '16rem', minWidth: 0,
    padding: tokens.spacingHorizontalM, borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  ratesHead: { color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold },
  rateRow: { display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, minWidth: 0 },
  rateAmount: { fontWeight: tokens.fontWeightSemibold },
  facts: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, flexGrow: 1, flexBasis: '14rem', minWidth: 0 },
  editLink: {
    color: tokens.colorBrandForeground1, textDecorationLine: 'none', fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300,
    display: 'inline-flex', alignItems: 'center',
    // Gate 6: a thumb target is at least 44×44 at phone width. It was a Button until it became a Link.
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
  footNote: { flexGrow: 1, flexBasis: '16rem', minWidth: 0, color: tokens.colorNeutralForeground2 },
});

export function ReviewOffer({ person, record, workflow, canEdit, onEditTerms, onReviewed, onBack }: {
  readonly person: Person;
  readonly record: OfferRecord;
  readonly workflow: HrWorkflowView;
  readonly canEdit: boolean;
  /** Opens the editor on this draft. Absent on the standalone route, where "Edit draft terms" is a link. */
  readonly onEditTerms?: () => void;
  /** Called once the offer is marked reviewed. The route goes on to 1.4; inline, the state moves by itself. */
  readonly onReviewed?: () => void | Promise<void>;
  /** Inline, the breadcrumb goes back to the draft summary instead of reloading the same URL. */
  readonly onBack?: () => void;
}) {
  const styles = useStyles();
  const id = person.id;
  const recordId = record.id;
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);
  const preview = useQuery({
    /**
     * The REVISION is part of the key. Without it, editing a draft's terms and coming back inside
     * the global 30s staleTime (main.tsx) served the previous build's bytes beside the new rates,
     * while Mark reviewed stayed enabled — HR certifying a document they were never shown.
     */
    queryKey: ['offer-preview', id, recordId, record.revision],
    queryFn: () => fetchOfferPreview(id, recordId),
    retry: false,
  });

  const markReviewed = useMutation({
    mutationFn: async () => performHrWorkflow(id, { operationId: crypto.randomUUID(), action: 'review-offer', recordId, expectedRevision: record.revision }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr-workflow', id] });
      await onReviewed?.();
    },
    // The server's refusal, verbatim. "Reviewed terms are immutable…" is wording HR has seen.
    onError: (error: Error) => setFailure(error.message),
  });

  const first = person.name.preferredName || person.name.givenName;
  const signed = workflow.offerSteps.some((s) => s.id === record.instanceId && s.state === 'approved');

  return (
    <OfferCard
      crumb={{ label: 'Offer and stage', href: `/app/people/${encodeURIComponent(id)}?section=hiring`, here: 'Review offer', onClick: onBack }}
      title="Review offer"
      lede={`See the offer exactly as ${first} will in the portal: the PDF, with fields to sign laid over the signing page. Check the rates, then mark it reviewed. Nothing is sent yet.`}
      reached={reachedStage(record.status, signed)}
      footer={<>
        <Caption1 className={styles.footNote}>Nothing is sent until you press Send offer.</Caption1>
        {/* NOT "Replace PDF". `buildOfferArtifact` creates the document from `offerTermsText`
            alone — there is no `PDFDocument.load` or `copyPages` in it — so no uploaded file is
            part of what is previewed or signed here. Editing the terms IS how this PDF changes,
            and on the one screen promising "exactly as they will see it" the control beside that
            promise must not name something that cannot change it. */}
        {canEdit && onEditTerms !== undefined ? <Button onClick={onEditTerms}>Edit draft terms</Button> : null}
        {canEdit && onEditTerms === undefined
          ? <Link to={`/app/people/${encodeURIComponent(id)}?section=hiring&draft=offer&record=${encodeURIComponent(recordId)}`} className={styles.editLink}>Edit draft terms</Link> : null}
        {canEdit ? <Button
          appearance="primary"
          // `preview.isSuccess`, not `!preview.isError`: pending is not shown either. Marking a
          // letter reviewed is certifying you read it, and this is the only screen that shows it.
          disabled={!canEdit || record.status !== 'draft' || !preview.isSuccess || markReviewed.isPending}
          onClick={() => { setFailure(null); markReviewed.mutate(); }}
        >
          {markReviewed.isPending ? 'Marking reviewed…' : 'Mark reviewed'}
        </Button> : null}
      </>}
    >
      <div className={styles.preview}>
        <div className={styles.caption}>
          <span className={styles.captionHead}>What {first} sees in the portal</span>
          <Caption1>
            After opening the link and entering the access code. There’s no separate web page for the letter:
            {' '}{first} reads and signs the PDF itself.
          </Caption1>
        </div>
        {failure === null ? null : <MessageBar intent="error"><MessageBarBody>{failure}</MessageBarBody></MessageBar>}
        <PortalPreview person={person} record={record} />
        <Caption1>
          {/* Not "a signing page is added at the end": `buildOfferArtifact` draws the acceptance
              box, the typed name and the date onto the LAST page of the letter itself, adding a
              page only when the wording leaves no room. Measured on a real offer: one page, all
              three fields on it. */}
          The whole letter is here. The acceptance box, typed name and signing date sit on its last page.
        </Caption1>
      </div>
      <div className={styles.checks}>
        <div className={styles.rates}>
          <span className={styles.ratesHead}>Rates you entered · check they match the letter</span>
          {record.terms.compensation.map((row) => (
            <div key={`${row.description}-${row.unit}`} className={styles.rateRow}>
              <Text>{row.description}</Text>
              <Text className={styles.rateAmount}>{rateText(row)}</Text>
            </div>
          ))}
        </div>
        <div className={styles.facts}>
          <OfferValues items={[
            { label: 'In the portal', value: preview.data === undefined
              ? 'The letter, signed on its last page'
              : `${preview.data.version.pageCount}-page letter, signed on its last page` },
            { label: 'Accept by', value: offerAcceptByText(record.terms.acceptanceDeadline) },
          ]} />
        </div>
        <div className={styles.facts}>
          <OfferValues items={[
            { label: 'Offer letter', value: `Drafted ${offerAcceptByText(record.createdAt)}` },
            { label: 'Signed copy filed to', value: fileCategoryLabels[record.terms.target] },
          ]} />
        </div>
      </div>
    </OfferCard>
  );
}
